import React, { useEffect } from 'react';
import { useState } from 'react';
import { CampaignFormSection, Frequency, TIMEZONES } from '../../enums/schedulingConstant';
import { Col, Row, Spacer, View } from '@amzn/stencil-react-components/layout';
import { FormattedMessage } from 'react-intl';

import { FormWrapper } from '@amzn/stencil-react-components/form';
import { Radio } from '@amzn/stencil-react-components/radio';
import { InputWrapper } from '@amzn/stencil-react-components/input';
import { Select } from '@amzn/stencil-react-components/select';
import { Checkbox } from '@amzn/stencil-react-components/checkbox';

import { Label, Text } from '@amzn/stencil-react-components/text';
import { useDispatch, useSelector } from 'react-redux';
import { updateCampaignForm } from '../../actions/createScheduleAction';
import { PopupDatePicker } from '@amzn/stencil-react-components/popup-date-picker';
import { campaignDateBuilder } from '../../util/campaign/campaignUtils';
import isEmpty from 'lodash/isEmpty';
import { accessByValue } from '../../util/journey/journeyUtils';

export const ScheduleSection = () => {
  const timezoneOptions = TIMEZONES;
  const campaignFormData = useSelector(store => store.campaign?.campaignFormState?.campaignDetails);
  const [startTime, setStartTime] = useState(campaignFormData?.startTime);
  const isSubmitSuccess = useSelector(store => store.campaign.campaignFormState.isSubmitSuccess);
  const [endTime, setEndTime] = useState(campaignFormData?.endTime);
  const [timeZone, setTimeZone] = useState(campaignFormData?.timeZone);
  const [frequency, setFrequency] = useState(campaignFormData?.frequency);
  const [endTimeError, setEndTimeError] = useState('');
  const [startTimeError, setStartTimeError] = useState('');
  const sendImmediate = useSelector(
    store => store?.campaign?.campaignFormState?.campaignDetails?.sendImmediate,
  );

  const timezoneValueAccessor = accessByValue;
  const timezoneRenderOption = option => (
    <Row gridGap="S200" alignItems="center">
      {option.label}
      <Text fontSize="T100" color="neutral70">
        {`(${option.value})`}
      </Text>
    </Row>
  );

  const dispatch = useDispatch();

  const onFrequencyChange = async e => {
    setFrequency(e);
    await dispatch(updateCampaignForm(CampaignFormSection.SCHEDULE, { frequency: e }));
  };

  const onStartTimeChange = e => {
    if (isEmpty(e)) return;
    const input = campaignDateBuilder(e);
    setStartTime(input);
    const start = new Date(input).getTime() ?? 0;
    const current = new Date().getTime();
    if (start < current) {
      setStartTimeError('Invalid date. Start date cannot be a past date');
      dispatch(updateCampaignForm(CampaignFormSection.SCHEDULE, { startTime: '' }));
      return;
    } else {
      setStartTimeError('');
      dispatch(updateCampaignForm(CampaignFormSection.SCHEDULE, { startTime }));
    }
  };
  const onEndTimeChange = e => {
    if (isEmpty(e)) return;
    const input = campaignDateBuilder(e);
    setEndTime(input);
    const end = new Date(input)?.getTime() ?? 0;
    const start = new Date(startTime)?.getTime() ?? 0;
    if (start > end) {
      setEndTimeError('Invalid date. End date should be after start date');
      dispatch(updateCampaignForm(CampaignFormSection.SCHEDULE, { endTime: '' }));
      return;
    } else {
      setEndTimeError('');
      dispatch(updateCampaignForm(CampaignFormSection.SCHEDULE, { endTime }));
    }
  };

  const onTimeZoneSelect = async e => {
    setTimeZone(e);
    await dispatch(
      updateCampaignForm(CampaignFormSection.SCHEDULE, {
        timeZone: {
          label: e.label,
          value: e.value,
        },
      }),
    );
  };

  const onTimeUpdate = async e => {
    dispatch(updateCampaignForm(CampaignFormSection.SCHEDULE, { sendImmediate: !sendImmediate }));
  };

  useEffect(
    () => {
      (async () => {
        if (sendImmediate) {
          await dispatch(
            updateCampaignForm(CampaignFormSection.SCHEDULE, {
              startTime: Frequency.IMMEDIATE,
              endTime: '',
              frequency: Frequency.ONCE,
            }),
          );
        }
      })();
    },
    [sendImmediate],
  );
  return (
    <>
      <Col gridGap="S200">
        <View display="flex" alignItems="center">
          <Checkbox
            disabled={isSubmitSuccess}
            id="immediate"
            data-testid="send-immediate-option"
            checked={sendImmediate}
            onChange={e => onTimeUpdate(e)}
          />
          <Spacer width={10} />
          <Label htmlFor="immediate">
            <FormattedMessage
              id="app.campaign.label.startCampaignImmediately"
              defaultMessage="Start campaign immediately"
            />
          </Label>
        </View>
        <Spacer height={30} />
      </Col>
      <Spacer height="S300" />
      <Col gridGap="S200">
        <Label fontSize={'T400'}>
          <FormattedMessage id="app.schedule.label.frequency" defaultMessage="Frequency" />
        </Label>
        <Spacer height="S300" />
        <FormWrapper>
          <View display="flex" alignItems="center">
            <Radio
              id="channel-type-email-once"
              data-testid="channel-type-once"
              name="output-format"
              disabled={isSubmitSuccess || sendImmediate}
              value={Frequency.ONCE}
              checked={frequency === Frequency.ONCE}
              onChange={() => {
                onFrequencyChange(Frequency.ONCE);
              }}
            />
            <Spacer width={30} />
            <Label fontSize={'T300'} htmlFor="channel-type-email-once">
              {Frequency.ONCE}
            </Label>
          </View>
          <View display="flex" alignItems="center">
            <Radio
              id="channel-type-email-hourly"
              data-testid="channel-type-hourly"
              name="output-format"
              disabled={isSubmitSuccess || sendImmediate}
              value={Frequency.HOURLY}
              checked={frequency === Frequency.HOURLY}
              onChange={() => {
                onFrequencyChange(Frequency.HOURLY);
              }}
            />
            <Spacer width={30} />
            <Label fontSize={'T300'} htmlFor="channel-type-email-once">
              {Frequency.HOURLY}
            </Label>
          </View>
          <View display="flex" alignItems="center">
            <Radio
              id="channel-type-email-daily"
              name="output-format"
              data-testid="channel-type-daily"
              value={Frequency.DAILY}
              disabled={isSubmitSuccess || sendImmediate}
              checked={frequency === Frequency.DAILY}
              onChange={() => {
                onFrequencyChange(Frequency.DAILY);
              }}
            />
            <Spacer width={30} />
            <Label fontSize={'T300'} htmlFor="channel-type-email-daily">
              {Frequency.DAILY}
            </Label>
          </View>
          <View display="flex" alignItems="center">
            <Radio
              id="channel-type-email-weekly"
              name="output-format"
              data-testid="channel-type-weekly"
              value={Frequency.WEEKLY}
              disabled={isSubmitSuccess || sendImmediate}
              checked={frequency === Frequency.WEEKLY}
              onChange={() => {
                onFrequencyChange(Frequency.WEEKLY);
              }}
            />
            <Spacer width={30} />
            <Label fontSize={'T300'} htmlFor="channel-type-email-weekly">
              {Frequency.WEEKLY}
            </Label>
          </View>
          <View display="flex" alignItems="center">
            <Radio
              id="frequency-type-email-monthly"
              name="output-format"
              data-testid="channel-type-monthly"
              value={Frequency.MONTHLY}
              disabled={isSubmitSuccess || sendImmediate}
              checked={frequency === Frequency.MONTHLY}
              onChange={() => {
                onFrequencyChange(Frequency.MONTHLY);
              }}
            />
            <Spacer width={30} />
            <Label fontSize={'T300'} htmlFor="channel-type-email-monthly">
              {Frequency.MONTHLY}
            </Label>
          </View>
        </FormWrapper>
      </Col>
      <Spacer height="S300" />
      <Col gridGap="S200">
        <InputWrapper
          maxWidth={300}
          id="datetime-with-validation"
          labelText="Start Time"
          htmlFor="campaign-start-time"
          footer={startTimeError}
          error={!!startTimeError}
          data-testid="start-date"
        >
          {inputProps => (
            <PopupDatePicker
              onChange={e => onStartTimeChange(e)}
              showTime={true}
              locale="iso8601"
              isDateDisabled={()=>{return sendImmediate || isSubmitSuccess}}
              id="campaign-start-time"
              dataTestId="campaign-start-time"
              labelText="some-datetime-input-label"
              {...inputProps}
            />
          )}
        </InputWrapper>
      </Col>
      <Spacer height="S300" />
      <Col gridGap="S200">
        {frequency !== Frequency.ONCE && (
          <InputWrapper
            htmlFor="campaign-end-time"
            labelText="End Time"
            footer={endTimeError}
            error={!!endTimeError}
          >
            {inputProps => (
              <PopupDatePicker
                onChange={e => onEndTimeChange(e)}
                showTime={true}
                locale="iso8601"
                isDateDisabled={()=>{return sendImmediate || isSubmitSuccess}}
                id="campaign-end-time"
                dataTestId="end-date"
                labelText="some-datetime-input-label"
                {...inputProps}
              />
            )}
          </InputWrapper>
        )}
      </Col>
      <Spacer height="S300" />
      <Col gridGap="S200">
        <Label htmlFor="some-select" id="some-select-label">
          <FormattedMessage id="app.campaign.label.timezone" defaultMessage="Timezones" />
        </Label>
        <Select
          id="some-select"
          data-testid="timezone-picker"
          labelId="some-select-label"
          onChange={e => {
            onTimeZoneSelect(e);
          }}
          options={timezoneOptions}
          disabled={isSubmitSuccess}
          valueAccessor={timezoneValueAccessor}
          optionToString={option => option.label}
          renderOption={timezoneRenderOption}
          value={timeZone}
        />
      </Col>
      <Spacer height="S300" />
    </>
  );
};
